import { api } from "#/lib/api-client";
import type {
  AdminDocumentFilters,
  DocumentAccessLogPageResponse,
  DocumentPageResponse,
  DocumentUploadRequest,
  MyDocumentFilters,
  StoredDocument,
} from "./types";

const BASE = "/api/hr/documents";

function qs(params: Record<string, unknown>): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      out.set(k, String(v));
    }
  }
  const s = out.toString();
  return s ? `?${s}` : "";
}

export const documentApi = {
  /**
   * Upload a document.
   * POST /api/hr/documents/upload  (multipart/form-data)
   * Requires HR_DOCUMENT_UPLOAD | HR_DOCUMENT_MANAGE | SUPER_ADMIN
   * moduleCode and referenceId are required by the backend.
   */
  upload(file: File, meta: DocumentUploadRequest): Promise<StoredDocument> {
    const form = new FormData();
    form.append("file", file);
    form.append("moduleCode", meta.moduleCode);
    form.append("referenceId", String(meta.referenceId));
    if (meta.documentType) form.append("documentType", meta.documentType);
    if (meta.visibility)   form.append("visibility",   meta.visibility);
    // Omit to default to "self" — the employee record of whoever is uploading.
    if (meta.employeeId != null) form.append("employeeId", String(meta.employeeId));
    return api
      .post<StoredDocument>(`${BASE}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  /**
   * List documents attached to a specific record.
   * GET /api/hr/documents?moduleCode=X&referenceId=Y
   * Returns DocumentResponse[] (array, NOT paged).
   * Requires HR_DOCUMENT_VIEW | HR_DOCUMENT_UPLOAD | HR_DOCUMENT_MANAGE | SUPER_ADMIN
   */
  listByRecord(
    moduleCode: string,
    referenceId: number,
  ): Promise<StoredDocument[]> {
    return api
      .get<StoredDocument[]>(`${BASE}${qs({ moduleCode, referenceId })}`)
      .then((r) => r.data);
  },

  /**
   * Admin global list — GET /api/hr/documents/all  (paged)
   * Requires HR_DOCUMENT_MANAGE | SUPER_ADMIN
   */
  listAll(
    params: AdminDocumentFilters & { page?: number; size?: number } = {},
  ): Promise<DocumentPageResponse> {
    return api
      .get<DocumentPageResponse>(`${BASE}/all${qs({ page: 0, size: 20, ...params })}`)
      .then((r) => r.data);
  },

  /**
   * Employee self-service — GET /api/hr/documents/my  (paged)
   * Any authenticated user. Returns only the current user's uploads.
   */
  listMine(
    params: MyDocumentFilters & { page?: number; size?: number } = {},
  ): Promise<DocumentPageResponse> {
    return api
      .get<DocumentPageResponse>(`${BASE}/my${qs({ page: 0, size: 20, ...params })}`)
      .then((r) => r.data);
  },

  /**
   * @deprecated This endpoint requires the Authorization header, so plain
   * `<a href>` / `window.open()` navigation to this URL will always 401 (the
   * browser doesn't attach the axios-managed Bearer token to a page navigation).
   * Use `downloadBlob()` instead, which goes through the authenticated client.
   */
  downloadUrl(id: number): string {
    return `${BASE}/${id}/download`;
  },

  /**
   * Fetches the file bytes through the authenticated axios client (so the
   * Authorization header is attached) and returns a Blob the UI can preview
   * or save. Every successful call is recorded server-side as a DOWNLOAD in
   * the access log — pair with `viewPing()` if you also want a VIEW recorded.
   * GET /api/hr/documents/{id}/download
   */
  downloadBlob(id: number): Promise<{ blob: Blob; contentType: string; filename: string | null }> {
    return api
      .get(`${BASE}/${id}/download`, { responseType: "blob" })
      .then((r) => {
        const contentType = (r.headers["content-type"] as string) ?? "application/octet-stream";
        const disposition = r.headers["content-disposition"] as string | undefined;
        const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : null;
        return { blob: r.data as Blob, contentType, filename };
      });
  },

  // NOTE: the generic `DELETE /api/hr/documents/{id}` endpoint was removed backend-side —
  // it let documents be deleted (e.g. a claim receipt) without the owning module's business
  // rules running (a closed claim could lose its receipt). Deletion now only happens through
  // each module's own document endpoint, which already enforces those rules — see e.g.
  // claims/api.ts `deleteClaimDocument(claimId, documentId)`. There is intentionally no
  // generic `remove()` here anymore.

  /**
   * Undo a soft delete — clears status back to ACTIVE. No time limit.
   * POST /api/hr/documents/{id}/restore
   * Requires HR_DOCUMENT_MANAGE | SUPER_ADMIN
   */
  restore(id: number): Promise<void> {
    return api.post(`${BASE}/${id}/restore`).then(() => undefined);
  },

  /**
   * Records a view — call when the in-app viewer opens a document (viewing doesn't
   * otherwise hit an endpoint the backend controls, unlike download).
   * POST /api/hr/documents/{id}/view-ping
   * Same visibility check as download. Fire-and-forget from the UI's perspective.
   */
  viewPing(id: number): Promise<void> {
    return api.post(`${BASE}/${id}/view-ping`).then(() => undefined);
  },

  /**
   * Who viewed/downloaded this document, and when.
   * GET /api/hr/documents/{id}/access-log  (paged)
   * Requires HR_DOCUMENT_MANAGE | SUPER_ADMIN
   */
  getAccessLog(
    id: number,
    page = 0,
    size = 10,
  ): Promise<DocumentAccessLogPageResponse> {
    return api
      .get<DocumentAccessLogPageResponse>(`${BASE}/${id}/access-log${qs({ page, size })}`)
      .then((r) => r.data);
  },
};
